// Servicio de validación lingüística. DESACOPLADO de la interfaz y del render.
// No contiene datos lingüísticos incrustados: usa el diccionario inyectado.
// Diccionario SEMILLA incrustado (palabras comunes) para juego inmediato y pruebas.
// El diccionario completo de la RAE (~636k palabras) se sirve como asset estático
// (public/dictionary-es.json) y se carga en caliente al arrancar (App.boot).
import dictionaryData from '../data/dictionary-seed-es.json';
import { normalizeForLookup, isValidSpanishWord, letterCount } from '../utils/unicode.js';
import { effectiveText } from './Tile.js';
import { TILE_TYPE } from '../utils/constants.js';

export class WordValidator {
  /**
   * @param {object} opts
   * @param {string[]} [opts.words] lista de palabras (se puede inyectar otra)
   * @param {number} [opts.minWordLength]
   */
  constructor({ words = dictionaryData.words, minWordLength = dictionaryData.minWordLength ?? 2 } = {}) {
    this.minWordLength = minWordLength;
    // Índice normalizado para búsqueda O(1).
    this.dictionary = new Set(words.map((w) => normalizeForLookup(w)));
    this._aiWords = null; // caché de la lista (array) para la búsqueda de la IA
  }

  /** Añade palabras al diccionario en caliente (ampliación). */
  addWords(words) {
    for (const w of words) this.dictionary.add(normalizeForLookup(w));
    this._aiWords = null;
  }

  /**
   * Carga palabras YA NORMALIZADAS (sin re-normalizar). Para el diccionario RAE
   * servido como asset (public/dictionary-es.json), que se genera normalizado.
   */
  addNormalizedWords(words) {
    for (const w of words) this.dictionary.add(w);
    this._aiWords = null;
  }

  /** Lista (array) de palabras normalizadas para la búsqueda de la IA. Cacheada:
   *  se reconstruye solo si el diccionario cambia. Misma referencia entre turnos
   *  para que el índice por longitud (aiSearch) y el worker la cacheen una sola vez. */
  aiWordList() {
    if (!this._aiWords) this._aiWords = [...this.dictionary];
    return this._aiWords;
  }

  get size() {
    return this.dictionary.size;
  }

  /** Normaliza una palabra para comparación. */
  normalizeWord(text) {
    return normalizeForLookup(text);
  }

  /** ¿Existe la palabra en el diccionario? */
  existsInDictionary(text) {
    return this.dictionary.has(normalizeForLookup(text));
  }

  /** Concatena el texto efectivo de una secuencia de fichas. */
  concatTiles(tiles) {
    return tiles.map((t) => effectiveText(t)).join('');
  }

  /**
   * Valida UNA palabra del tablero (secuencia de fichas).
   * @param {object[]} tiles
   * @returns {{ valid:boolean, word:string, reason?:string, code?:string }}
   */
  validateWord(tiles) {
    if (!tiles || tiles.length === 0) {
      return { valid: false, word: '', code: 'empty', reason: 'La palabra está vacía.' };
    }

    // 1) Comodines universales deben tener valor asignado.
    for (const t of tiles) {
      if (t.type === TILE_TYPE.WILDCARD && t.wildKind === 'universal' && !t.assignedText) {
        return { valid: false, word: '', code: 'wildcard', reason: 'El comodín necesita una letra asignada.' };
      }
    }

    const word = this.concatTiles(tiles);

    // 2) Caracteres válidos.
    if (!isValidSpanishWord(word)) {
      return { valid: false, word, code: 'chars', reason: 'La palabra contiene caracteres no válidos.' };
    }

    // 3) Longitud mínima.
    if (letterCount(word) < this.minWordLength) {
      return {
        valid: false,
        word,
        code: 'short',
        reason: `La palabra debe tener al menos ${this.minWordLength} letras.`,
      };
    }

    // 4) Prefijos al comienzo, sufijos al final.
    for (let i = 0; i < tiles.length; i++) {
      const t = tiles[i];
      if (t.type === TILE_TYPE.PREFIX && i !== 0) {
        return { valid: false, word, code: 'prefix', reason: `El prefijo ${t.text} debe estar al comienzo.` };
      }
      if (t.type === TILE_TYPE.SUFFIX && i !== tiles.length - 1) {
        return { valid: false, word, code: 'suffix', reason: `El sufijo ${t.text} debe estar al final.` };
      }
    }

    // 5) Existencia en diccionario.
    if (!this.existsInDictionary(word)) {
      return {
        valid: false,
        word,
        code: 'dictionary',
        reason: 'Esta palabra no está incluida en el diccionario.',
      };
    }

    return { valid: true, word };
  }

  /**
   * Valida TODO el tablero. Al confirmar el turno, todas las palabras deben ser válidas.
   * @param {Array<{id:string, tiles:object[]}>} boardWords
   * @returns {{ valid:boolean, results:Array, firstError?:object }}
   */
  validateBoard(boardWords) {
    const results = [];
    let firstError = null;
    for (const bw of boardWords) {
      const res = this.validateWord(bw.tiles);
      results.push({ id: bw.id, ...res });
      if (!res.valid && !firstError) firstError = { id: bw.id, ...res };
    }
    return { valid: !firstError, results, firstError };
  }

  /** Devuelve una explicación legible de por qué una palabra no es válida. */
  explainInvalidWord(tiles) {
    const res = this.validateWord(tiles);
    if (res.valid) return null;
    return res.reason;
  }
}
